/**
 * Cuándo el centro está cerrado un día.
 *
 * No basta el plan semanal: un festivo o unas vacaciones en las que no queda
 * nadie también cierran. El centro abre si ALGUIEN tiene jornada residual
 * ese día; si a todos les come el cierre, el día está cerrado.
 */
import { jornadaDe, restarIntervalos, sumarDias, type PlanSemanal } from './calendario-geometria'

export type PeriodoCierre = { profesionalId: number; inicio: string; fin: string }
export type ProfesionalDelCentro = { id: number; plan: PlanSemanal }

const RE_MARCA = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}):(\d{2}))?/

function minutosDe(hh: string, mm: string): number {
    return Number(hh) * 60 + Number(mm)
}

/** Inicio del periodo: fecha sola = medianoche de ese día. */
export function inicioCierre(marca: string): string | null {
    const m = RE_MARCA.exec(marca)
    if (!m?.[1]) return null
    return `${m[1]} ${m[2] ?? '00'}:${m[3] ?? '00'}:00`
}

/** Fin del periodo: fecha sola = medianoche del día siguiente (ese día entra entero). */
export function finCierre(marca: string): string | null {
    const m = RE_MARCA.exec(marca)
    if (!m) return null
    const dia = m[1]
    if (!dia) return null
    if (!m[2]) return `${sumarDias(dia, 1)} 00:00:00`
    return `${dia} ${m[2]}:${m[3] ?? '00'}:00`
}

/**
 * Qué parte del día tapa un periodo. `null` si no pisa el día.
 * El final es exclusivo en minutos: 18:00 deja libre desde las 18:00.
 */
export function cierreEnDia(inicio: string, fin: string, dia: string): { desdeMin: number; hastaMin: number } | null {
    const a = inicioCierre(inicio)
    const b = finCierre(fin)
    if (!a || !b) return null
    const diaIni = `${dia} 00:00:00`
    const diaFin = `${sumarDias(dia, 1)} 00:00:00`
    if (a >= diaFin || b <= diaIni) return null
    const desdeMin = a <= diaIni ? 0 : minutosDe(a.slice(11, 13), a.slice(14, 16))
    const hastaMin = b >= diaFin ? 24 * 60 : minutosDe(b.slice(11, 13), b.slice(14, 16))
    if (hastaMin <= desdeMin) return null
    return { desdeMin, hastaMin }
}

export function profesionalTrabajaEseDia(
    plan: PlanSemanal,
    profesionalId: number,
    cierres: PeriodoCierre[],
    dia: string,
): boolean {
    const jornada = jornadaDe(plan, dia)
    if (jornada.length === 0) return false
    const quita = []
    for (const c of cierres) {
        if (c.profesionalId !== profesionalId) continue
        const trozo = cierreEnDia(c.inicio, c.fin, dia)
        if (trozo) quita.push(trozo)
    }
    return restarIntervalos(jornada, quita).length > 0
}

/**
 * El centro está cerrado si no queda nadie que pueda atender.
 * Sin profesionales todavía no se sabe: no se pinta cerrado.
 */
export function diaCerradoDelCentro(
    profesionales: ProfesionalDelCentro[],
    cierres: PeriodoCierre[],
    dia: string,
): boolean {
    if (profesionales.length === 0) return false
    return !profesionales.some((p) => profesionalTrabajaEseDia(p.plan, p.id, cierres, dia))
}

/** Se esconde solo si está cerrado y no tiene citas (una cita no puede desaparecer). */
export function seOcultaElDia(ocultar: boolean, cerrado: boolean, tieneCitas: boolean): boolean {
    return ocultar && cerrado && !tieneCitas
}

/** Índices 0 = lunes … 6 = domingo que se pintan en el mes. */
export function columnasMesQueSeVen(
    celdas: string[],
    cerrado: (dia: string) => boolean,
    tieneCitas: (dia: string) => boolean,
    ocultar: boolean,
): number[] {
    const todos = [0, 1, 2, 3, 4, 5, 6]
    if (!ocultar) return todos
    return todos.filter((col) => {
        for (let fila = 0; fila < 6; fila++) {
            const d = celdas[fila * 7 + col]
            if (d && !seOcultaElDia(true, cerrado(d), tieneCitas(d))) return true
        }
        return false
    })
}

/** Filas 0…5 del mes que todavía tienen algún día visible. */
export function filasMesQueSeVen(
    celdas: string[],
    columnas: number[],
    cerrado: (dia: string) => boolean,
    tieneCitas: (dia: string) => boolean,
    ocultar: boolean,
): number[] {
    const todas = [0, 1, 2, 3, 4, 5]
    if (!ocultar) return todas
    return todas.filter((fila) =>
        columnas.some((col) => {
            const d = celdas[fila * 7 + col]
            return Boolean(d && !seOcultaElDia(true, cerrado(d), tieneCitas(d)))
        }),
    )
}
